/**
 * Activity / Breach Readiness - Phase 3 wiring.
 *
 * Live data source: GET /v1/activity/breach-readiness
 *
 * Renders the live `rbi6HourClock` block (RBI's 6-hour mandate is the
 * strictest reportable-breach clock in India) plus any open / recent-
 * closed incidents the engine emits. Falls back to a clean empty
 * state when the tenant has zero incidents.
 *
 * The page does NOT manufacture an incident when none exist - the
 * earlier cut hardcoded a fake "BLK-9F8E7D6C5B4A" that an evaluator
 * would have triaged as real. Deleted in the Phase-3 wiring pass.
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { gsap } from 'gsap';
import { Timer, FileText, Users, Database, AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { getBreachReadiness, type BreachReadiness } from '@/lib/api';

interface ClockProps {
  totalSeconds: number;
  elapsedSeconds: number;
  label: string;
  regulator: string;
  primary?: boolean;
}

function BreachClock({ totalSeconds, elapsedSeconds: initialElapsed, label, regulator, primary }: ClockProps) {
  const [elapsed, setElapsed] = useState(initialElapsed);
  const ringRef = useRef<SVGCircleElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => Math.min(totalSeconds, e + 1)), 1000);
    return () => clearInterval(t);
  }, [totalSeconds]);

  const remaining = Math.max(0, totalSeconds - elapsed);
  const progress = elapsed / totalSeconds;
  const r = 70;
  const c = 2 * Math.PI * r;

  useEffect(() => {
    if (!ringRef.current) return;
    gsap.to(ringRef.current, {
      strokeDashoffset: c - c * progress,
      duration: 0.8,
      ease: 'power3.out',
    });
  }, [progress, c]);

  useEffect(() => {
    if (!numberRef.current) return;
    gsap.fromTo(
      numberRef.current,
      { scale: 1.05, opacity: 0.7 },
      { scale: 1, opacity: 1, duration: 0.4, ease: 'power2.out' },
    );
  }, [Math.floor(remaining)]);

  const danger = progress > 0.66;
  const warn = progress > 0.33;

  return (
    <div className={`relative ${primary ? 'p-4' : 'p-2'}`}>
      <svg width={primary ? 220 : 150} height={primary ? 220 : 150} viewBox="0 0 200 200">
        <defs>
          <linearGradient id={`grad-${regulator}`} x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={danger ? 'hsl(var(--sev-critical))' : warn ? 'hsl(var(--sev-medium))' : 'hsl(var(--success))'} />
            <stop offset="100%" stopColor={danger ? 'hsl(var(--sev-high))' : warn ? 'hsl(var(--sev-high))' : 'hsl(var(--chart-2))'} />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r={r} stroke="hsl(var(--muted))" strokeWidth="8" fill="none" />
        <circle
          ref={ringRef}
          cx="100"
          cy="100"
          r={r}
          stroke={`url(#grad-${regulator})`}
          strokeWidth="8"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c}
          strokeLinecap="round"
          transform="rotate(-90 100 100)"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div ref={numberRef} className="text-center">
          <div className={`tabular ${primary ? 'text-4xl' : 'text-2xl'} font-display font-bold leading-none`}>
            {formatHMS(remaining)}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
          <div className={`text-[10px] mt-0.5 font-medium ${danger ? 'text-sev-critical' : warn ? 'text-sev-medium' : 'text-success'}`}>
            {regulator}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatHMS(seconds: number): string {
  if (seconds <= 0) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function ActivityBreachReadiness() {
  const [data, setData] = useState<BreachReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = (signal?: AbortSignal) =>
    getBreachReadiness()
      .then((r) => !signal?.aborted && setData(r))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const openIncidents = data?.openIncidents ?? [];
  const firstOpen = openIncidents[0] ?? null;
  const recentClosed = data?.recentClosed ?? [];

  // Compute clock elapsed from the open incident's detectedAt, OR fall
  // back to the rbi6HourClock.minutesSinceLastDetection when the
  // engine doesn't carry per-incident timing.
  const rbiElapsedSeconds = firstOpen?.detectedAt
    ? Math.floor((Date.now() - new Date(firstOpen.detectedAt).getTime()) / 1000)
    : data?.rbi6HourClock?.minutesSinceLastDetection != null
      ? data.rbi6HourClock.minutesSinceLastDetection * 60
      : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Breach Readiness"
        subtitle="Multi-regulator breach notification clocks. RBI 6h is the strictest in India. Detection module auto-fills DPB notifications when an incident is confirmed."
        badges={[
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : openIncidents.length > 0
                ? [{ label: `${openIncidents.length} open incident${openIncidents.length === 1 ? '' : 's'}`, variant: 'critical' as const }]
                : [{ label: 'No open incidents', variant: 'success' as const }]),
        ]}
      />

      {loading && <LoadingState caption="Loading breach-readiness state…" />}
      {error && !loading && (
        <ErrorState
          title="Failed to load breach-readiness state"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Open incidents"
              value={openIncidents.length}
              icon={AlertTriangle}
              glow={openIncidents.length > 0 ? 'critical' : 'low'}
            />
            <KpiCard
              label="Recent breaches (90d)"
              value={data.recentBreaches ?? 0}
              icon={Timer}
              hint="from ClickHouse breach_incidents"
            />
            <KpiCard
              label="Avg detection (min)"
              value={data.averageDetectionMinutes ?? 0}
              icon={ShieldCheck}
              hint="across tenant history"
            />
            <KpiCard
              label="Avg containment (min)"
              value={data.averageContainmentMinutes ?? 0}
              icon={FileText}
              hint="block-to-contained"
            />
          </div>

          {openIncidents.length === 0 ? (
            <GlassCard className="p-8 text-center">
              <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-success opacity-70" />
              <CardTitle className="text-base">No open incidents</CardTitle>
              <CardDescription className="text-sm mt-1">
                When Detection escalates a confirmed breach, the regulator clocks below will start counting from the detection timestamp.
              </CardDescription>
              <div className="text-[10px] text-muted-foreground mt-3">
                Last refresh: <code className="font-mono text-foreground/80">{new Date(data.generatedAt).toLocaleString()}</code>
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="p-8 mb-6 aurora">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <CardTitle className="text-xl">⚠ ACTIVE INCIDENT - {firstOpen!.id.slice(0, 18)}…</CardTitle>
                  <CardDescription className="text-sm mt-1">
                    Severity <strong>{firstOpen!.severity}</strong>. Detected at{' '}
                    <code>{new Date(firstOpen!.detectedAt).toLocaleString()}</code>.
                  </CardDescription>
                </div>
                <Badge variant="critical" className="text-xs">CONFIRMED</Badge>
              </div>

              <div className="flex items-center justify-center mb-6">
                <BreachClock
                  totalSeconds={6 * 3600}
                  elapsedSeconds={Math.max(0, rbiElapsedSeconds)}
                  label="Time remaining"
                  regulator="RBI 6-hour clock"
                  primary
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ClockTile
                  name="DPDPA Sec 8(7)"
                  totalH={72}
                  elapsedH={Math.max(0, rbiElapsedSeconds / 3600)}
                  icon={FileText}
                />
                <ClockTile
                  name="GDPR Art 33"
                  totalH={72}
                  elapsedH={Math.max(0, rbiElapsedSeconds / 3600)}
                  icon={FileText}
                />
                <ClockTile
                  name="HIPAA §164.408"
                  totalH={60 * 24}
                  elapsedH={Math.max(0, rbiElapsedSeconds / 3600)}
                  icon={FileText}
                />
                <ClockTile
                  name="PIPL"
                  totalH={24}
                  elapsedH={Math.max(0, rbiElapsedSeconds / 3600)}
                  icon={FileText}
                />
              </div>
            </GlassCard>
          )}

          {recentClosed.length > 0 && (
            <GlassCard className="p-5 mt-4">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="text-sm">Recently closed incidents</CardTitle>
                <CardDescription className="text-xs">Closed in the last 90 days - used to calibrate MTTD/MTTR averages above.</CardDescription>
              </CardHeader>
              <div className="space-y-1.5">
                {recentClosed.slice(0, 8).map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-xs glass-lite rounded-md px-3 py-2">
                    <code className="font-mono">{r.id.slice(0, 18)}…</code>
                    <span className="text-muted-foreground">closed {new Date(r.closedAt).toLocaleDateString()}</span>
                    <Badge variant="glass" className="text-[10px]">{r.outcome}</Badge>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </>
      )}
    </PageContainer>
  );
}

function ClockTile({ name, totalH, elapsedH, icon: Icon }: { name: string; totalH: number; elapsedH: number; icon: React.ComponentType<{ className?: string }> }) {
  const remaining = Math.max(0, totalH - elapsedH);
  const progress = totalH > 0 ? elapsedH / totalH : 0;
  const danger = progress > 0.66;
  const warn = progress > 0.33;

  return (
    <div className="glass-lite p-4 rounded-md text-center">
      <Icon className={`h-4 w-4 mx-auto mb-1 ${danger ? 'text-sev-critical' : warn ? 'text-sev-medium' : 'text-success'}`} />
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{name}</div>
      <div className={`text-lg font-display font-bold tabular mt-1 ${danger ? 'text-sev-critical' : warn ? 'text-sev-medium' : 'text-success'}`}>
        {remaining.toFixed(1)}h
      </div>
      <div className="text-[10px] text-muted-foreground">remaining</div>
      <div className="h-1 bg-muted rounded-full overflow-hidden mt-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.7 }}
          className={`h-full ${danger ? 'bg-sev-critical' : warn ? 'bg-sev-medium' : 'bg-success'}`}
        />
      </div>
    </div>
  );
}
